/**
 * Primary hook for ViewJob to access all Ghostwriter-related data.
 *
 * Encapsulates:
 * - Proctor gating (rn_ghostwriter_test must be active)
 * - Eligibility lookup (shared autoAppliableJobKeys set; the live-read buckets
 *   read per visit)
 * - Application data fetch (always re-fetches on every render)
 * - Consent status resolution (via useResolveConsentStatus)
 * - Button state computation for ViewJob CTA rendering
 *
 * ViewJob-first scenario: if the eligibility set hasn't been hydrated
 * by Homepage yet, falls back to a single-job eligibility + application
 * fetch.
 *
 * deprecated variant: the payload's `jobKeys` is the eligibility source (skip
 * the backend check); consent-exempt.
 *
 * sync_prefill_submit / sync_auto_apply variant: eligibility comes from a live
 * per-visit read (the payload is a traffic-cap allowlist, not the source of
 * truth). The result lives in component state, so it dies with the visit. All
 * three buckets are consent-exempt.
 *
 * Two-step by design (do not collapse): the eligibility read only records the
 * result; flipping `canFetchApplication` is what fetches the application on
 * re-entry. Doing both in the read would double-fetch.
 *
 * Consent optimization: the consent check is gated on eligibility, so
 * ineligible jobs never trigger the consent network call.
 */

import { loadingAsyncState, successAsyncState, errorAsyncState } from "./AsyncState";
import { useGhostwriterApi } from "./ghostwriterApi";
import {
  useIsJobAutoAppliable,
  useIsJobInEligibilityAllowlist,
  useGhostwriterJobByKey,
} from "./GhostwriterContext";
import {
  addJobToStore,
  addJobKeyToAutoAppliableSet,
  setJobsState,
  removeJobFromStore,
  getSnapshot,
} from "./GhostwriterStore";
import { useGhostwriterGate } from "./useGhostwriterGate";
import { useResolveConsentStatus } from "./useResolveConsentStatus";
import { useMinDurationLoading } from "../hooks/useMinDurationLoading";
import {
  type ScheduleApplyJob,
  type ScheduleApplyStatus,
  ScheduleApplyStatuses,
  ScheduleApplyStatusSeverity,
} from "../models/ScheduleApplyJob";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type GhostwriterButtonAction =
  | "none"
  | "loading"
  | "try-auto-apply"
  | "auto-apply"
  | "in-progress"
  | "ready-for-review"
  | "applied"
  | "error";

export const GHOSTWRITER_PRIMARY_ACTIONS: GhostwriterButtonAction[] = [
  "try-auto-apply",
  "auto-apply",
  "in-progress",
  "ready-for-review",
  "applied",
];

export function shouldShowSecondaryApply(buttonAction: GhostwriterButtonAction): boolean {
  return buttonAction !== "applied" && GHOSTWRITER_PRIMARY_ACTIONS.includes(buttonAction);
}

/**
 * RN-8455: whether tapping the ZIA Auto apply CTA should also run the shared
 * third-party apply-intent capture. Only for a third-party (non-Indeed-Apply)
 * job in the `"auto-apply"` state whose OneGraph model has a `thirdPartyApply`.
 */
export function shouldCaptureThirdPartyApplyIntent(
  buttonAction: GhostwriterButtonAction,
  isIndeedApplyJob: boolean,
  hasThirdPartyApply: boolean,
): boolean {
  return buttonAction === "auto-apply" && !isIndeedApplyJob && hasThirdPartyApply;
}

const STATUS_ACTION_MAP = new Map<ScheduleApplyStatus, GhostwriterButtonAction>([
  [ScheduleApplyStatuses.NotStarted, "auto-apply"],
  [ScheduleApplyStatuses.AutoApplyCancelled, "auto-apply"],
  [ScheduleApplyStatuses.AnalyzingJob, "in-progress"],
  [ScheduleApplyStatuses.FillingOutApplication, "in-progress"],
  [ScheduleApplyStatuses.SubmittingApplication, "in-progress"],
  [ScheduleApplyStatuses.RetryPendingCaptcha, "in-progress"],
  [ScheduleApplyStatuses.ReadyToReview, "ready-for-review"],
  [ScheduleApplyStatuses.MissingRequiredInfo, "ready-for-review"],
  [ScheduleApplyStatuses.Applied, "applied"],
  [ScheduleApplyStatuses.AutoApplyFailed, "error"],
  [ScheduleApplyStatuses.SubmissionFailed, "error"],
]);

function computeButtonAction(
  isEnabled: boolean | null,
  isAutoAppliable: boolean,
  hasConsented: boolean,
  consentLoading: boolean,
  ghostwriterJob: ScheduleApplyJob | null,
  appliableLoading: boolean,
  consentExempt: boolean,
): GhostwriterButtonAction {
  if (isEnabled !== true) return "none";
  if (appliableLoading) return "loading";
  if (!isAutoAppliable) return "none";
  // Consent-exempt buckets (sync_prefill_submit/deprecated/sync_auto_apply) show the CTA on eligibility alone
  // while consent resolves in the background.
  if (!consentExempt && consentLoading) return "loading";
  if (!hasConsented) return "try-auto-apply";
  if (!ghostwriterJob) return "auto-apply";

  const { applicationStatus } = ghostwriterJob;
  return (
    STATUS_ACTION_MAP.get(applicationStatus) ??
    (applicationStatus.severity === ScheduleApplyStatusSeverity.Error ? "error" : "in-progress")
  );
}

export interface GhostwriterViewJobResult {
  buttonAction: GhostwriterButtonAction;
  applicationId: string | null;
  /**
   * True if the user is in the sync_prefill_submit (synchronous flow) bucket.
   * Consumers branch CTA copy on this flag — see RN-3662.
   */
  isSyncPrefill: boolean;
  /**
   * True if the user is in the deprecated bucket (payload fast-path); uses the
   * synchronous Prefill UI flow on ViewJob. See RN-5091.
   */
  isDeprecated: boolean;
  /** True if in the sync_auto_apply bucket. Same routing/CTA as deprecated;
   * consumers gate the synchronous Prefill flow on it alongside deprecated. */
  isSyncAutoApply: boolean;
  isAutoApplyLoading: boolean;
  beginAutoApplyLoading: () => void;
  /**
   * True when the user should get sync_prefill_submit's "Prefill" ViewJob
   * experience (CTA copy, prefilling modal entry). hybrid_automation is a
   * sync_prefill_submit clone that also adds the in-review intervention screen,
   * so on ViewJob it must look identical. Prefer this over `isSyncPrefill` for
   * any Prefill-experience branch. See RN-3367.
   */
  usesPrefillExperience: boolean;
  startAutoApply: () => Promise<void>;
  stopAutoApply: () => void;
  /**
   * Re-fetches the ghostwriter application for this job and writes it
   * into the shared store. Used when the prefilling modal dismisses so
   * ViewJob can show the latest CTA state.
   */
  refreshApplication: () => Promise<void>;
}

export function useGhostwriterForViewJob(jobKey: string | null): GhostwriterViewJobResult {
  const { isEnabled, isSyncPrefill, isDeprecated, isHybridAutomation, isSyncAutoApply } =
    useGhostwriterGate();
  const {
    fetchAutoAppliableJobKeys,
    fetchApplicationForJob,
    fetchEligibilityAndApplication,
    startScheduleApply,
  } = useGhostwriterApi();
  const {
    isAutoAppliable: storeAutoAppliable,
    loading: appliableLoading,
    hydrated,
  } = useIsJobAutoAppliable(jobKey);
  // sync_prefill_submit and sync_auto_apply both determine eligibility via a
  // live per-visit read capped by the payload allowlist (not pre-seeded).
  const isLiveEligibilityBucket = isSyncPrefill === true || isSyncAutoApply === true;
  // The allowlist caps which jobs run the real-time read.
  const isInEligibilityAllowlist = useIsJobInEligibilityAllowlist(jobKey);

  // Paired with the jobKey it was read for, so a jobKey change invalidates it
  // during render. `null` = not read yet, which renders as ineligible.
  const [eligibilityResult, setEligibilityResult] = useState<{
    jobKey: string;
    eligible: boolean;
  } | null>(null);
  const isCurrentJobEligible =
    eligibilityResult?.jobKey === jobKey ? eligibilityResult.eligible : null;

  const isAutoAppliable = isLiveEligibilityBucket
    ? isCurrentJobEligible === true
    : storeAutoAppliable;
  // `hydrated` tracks the shared store, which the live-read buckets no longer write.
  const canFetchApplication = isLiveEligibilityBucket
    ? isCurrentJobEligible === true
    : hydrated && isAutoAppliable;

  const { job: ghostwriterJob } = useGhostwriterJobByKey(jobKey);
  // Ineligible jobs never trigger the consent network call.
  const shouldResolveConsent = isEnabled === true && isAutoAppliable;
  const { hasConsented, consentLoading } = useResolveConsentStatus(shouldResolveConsent);

  // Bumped on each read so stale responses can be ignored
  const applicationReadGenerationRef = useRef(0);
  // jobKey this instance has already issued its live eligibility read for.
  const eligibilityReadJobKeyRef = useRef<string | null>(null);
  // Bumped on each live eligibility read so out-of-order responses can be ignored.
  const eligibilityReadGenerationRef = useRef(0);

  useEffect(() => {
    if (!isEnabled || !jobKey) return;

    if (canFetchApplication) {
      // Also serves the live-read buckets: once its eligibility read records an eligible
      // result, this branch fetches the application on re-entry.
      const generation = ++applicationReadGenerationRef.current;
      setJobsState(loadingAsyncState(getSnapshot().jobs.data));
      fetchApplicationForJob(jobKey)
        .then((job) => {
          if (applicationReadGenerationRef.current !== generation) return;
          if (job) {
            addJobToStore(job);
          } else {
            // Application no longer exists, drop the stale entry
            removeJobFromStore(jobKey);
          }
        })
        .catch((err: Error) => {
          if (applicationReadGenerationRef.current !== generation) return;
          setJobsState(errorAsyncState(err, getSnapshot().jobs.data));
        });
    } else if (!appliableLoading && !isSyncPrefill && !isDeprecated && isSyncAutoApply !== true) {
      // deprecated trusts its payload; sync_prefill_submit/sync_auto_apply use
      // their own per-visit eligibility read below. All other enabled buckets
      // use the combined read.
      const generation = ++applicationReadGenerationRef.current;
      setJobsState(loadingAsyncState(getSnapshot().jobs.data));
      fetchEligibilityAndApplication(jobKey)
        .then((result) => {
          if (applicationReadGenerationRef.current !== generation) return;
          if (result.eligible) {
            addJobKeyToAutoAppliableSet(jobKey);
            if (result.application) {
              addJobToStore(result.application);
            } else {
              // Eligible but the application is gone, clear the stale entry
              removeJobFromStore(jobKey);
            }
          } else {
            setJobsState(successAsyncState(getSnapshot().jobs.data ?? []));
          }
        })
        .catch((err: Error) => {
          if (applicationReadGenerationRef.current !== generation) return;
          setJobsState(errorAsyncState(err, getSnapshot().jobs.data));
        });
    }

    // Bump on teardown too, so a read still in flight at unmount is ignored
    return () => {
      applicationReadGenerationRef.current += 1;
    };
  }, [
    isEnabled,
    isSyncPrefill,
    isDeprecated,
    isSyncAutoApply,
    jobKey,
    canFetchApplication,
    // Folded into `canFetchApplication`, but kept so the non-live-read re-run
    // surface is unchanged.
    hydrated,
    isAutoAppliable,
    appliableLoading,
    fetchApplicationForJob,
    fetchEligibilityAndApplication,
  ]);

  // RN-8048: one eligibility read per ViewJob visit per allowlisted job. Kept
  // out of the effect above, which would otherwise never re-run to fetch the
  // application when a read confirms the result it already had.
  useEffect(() => {
    if (!isEnabled || !jobKey || !isLiveEligibilityBucket || !isInEligibilityAllowlist) {
      // Not a read target. Forget the last read so returning to that job
      // counts as a new visit, and drop its result so it can't be re-shown.
      eligibilityReadJobKeyRef.current = null;
      // Retire any in-flight read too, or it could land while we are off-target
      // and restore a result for the job we just left.
      eligibilityReadGenerationRef.current += 1;
      setEligibilityResult((current) => (current === null ? current : null));
      return;
    }
    // Per-instance, so StrictMode's double-invoke is covered but a genuine
    // remount still reads — its result lands in its own state and cannot be
    // handed over from the abandoned one.
    if (eligibilityReadJobKeyRef.current === jobKey) return;
    eligibilityReadJobKeyRef.current = jobKey;
    // Returning via an allowlisted job skips the clear above, so clear here too.
    setEligibilityResult(null);

    // By generation, not jobKey: A -> B -> A must still discard A's first read.
    const generation = ++eligibilityReadGenerationRef.current;
    const isStale = () => eligibilityReadGenerationRef.current !== generation;
    fetchAutoAppliableJobKeys([jobKey])
      .then((keys) => {
        if (isStale()) return;
        setEligibilityResult({ jobKey, eligible: keys.includes(jobKey) });
      })
      // A failed read is "not eligible this visit"; the next visit retries.
      .catch(() => {
        if (isStale()) return;
        setEligibilityResult({ jobKey, eligible: false });
      });
  }, [
    isEnabled,
    isLiveEligibilityBucket,
    isInEligibilityAllowlist,
    jobKey,
    fetchAutoAppliableJobKeys,
  ]);

  const {
    isLoading: isAutoApplyLoading,
    start: beginAutoApplyLoading,
    stop: stopAutoApply,
  } = useMinDurationLoading();

  const startAutoApply = useCallback(async () => {
    if (!jobKey) throw new Error("Cannot start auto apply without a job key");
    beginAutoApplyLoading();
    try {
      await startScheduleApply(jobKey);
    } catch (err) {
      stopAutoApply();
      throw err;
    }
  }, [jobKey, startScheduleApply, beginAutoApplyLoading, stopAutoApply]);

  const refreshApplication = useCallback(async () => {
    if (!jobKey) return;

    const generation = ++applicationReadGenerationRef.current;
    try {
      const job = await fetchApplicationForJob(jobKey);
      if (applicationReadGenerationRef.current !== generation) return;
      if (job) {
        addJobToStore(job);
      } else {
        // Application no longer exists, drop the stale entry
        removeJobFromStore(jobKey);
      }
    } catch (err) {
      console.warn("[useGhostwriterForViewJob] refreshApplication failed", err);
    }
  }, [jobKey, fetchApplicationForJob]);

  const buttonAction = useMemo(() => {
    // sync_prefill_submit, deprecated, and sync_auto_apply don't block the CTA on consent (see
    // computeButtonAction).
    const consentExempt =
      isSyncPrefill === true || isDeprecated === true || isSyncAutoApply === true;
    return computeButtonAction(
      isEnabled,
      isAutoAppliable,
      hasConsented,
      consentLoading,
      ghostwriterJob,
      appliableLoading,
      consentExempt,
    );
  }, [
    isEnabled,
    isAutoAppliable,
    hasConsented,
    consentLoading,
    ghostwriterJob,
    appliableLoading,
    isSyncPrefill,
    isDeprecated,
    isSyncAutoApply,
  ]);

  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    stopAutoApply();
  }, [buttonAction, stopAutoApply]);

  return {
    buttonAction,
    applicationId: ghostwriterJob?.applicationId ?? null,
    isSyncPrefill: isSyncPrefill === true,
    isDeprecated: isDeprecated === true,
    isSyncAutoApply: isSyncAutoApply === true,
    isAutoApplyLoading,
    beginAutoApplyLoading,
    usesPrefillExperience: isSyncPrefill === true || isHybridAutomation === true,
    startAutoApply,
    stopAutoApply,
    refreshApplication,
  };
}
